/**
 * API helper functions for branding management
 */

import { addCSRFHeader } from '@/utils/csrf'
import { fetchWithRefresh } from '@/utils/fetchWithRefresh'

interface BrandingConfig {
  contact_info: {
    company_name?: string
    primary_email?: string
    phone?: string
    address?: string
    emergency_phone?: string
    emergency_email?: string
    support_hours?: string
    emergency_availability?: string
  }
  messaging: {
    company_tagline?: string
    security_mission?: string
    value_propositions?: string[]
    call_to_action?: string
    report_disclaimer?: string
  }
  branding_assets: {
    logo_filename?: string
    favicon_filename?: string
  }
  color_scheme: {
    primary_color?: string
    secondary_color?: string
    success_color?: string
    warning_color?: string
    danger_color?: string
    background_color?: string
    text_color?: string
    light_text_color?: string
    border_color?: string
  }
  custom_css?: string
  is_active?: boolean
}

/**
 * Get current branding configuration
 */
export async function getBrandingConfig(): Promise<BrandingConfig | null> {
  try {
    const response = await fetchWithRefresh('/api/agency-branding/config', {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      }
    })

    if (!response.ok) {
      throw new Error(`Failed to fetch branding config: ${response.status}`)
    }

    const data = await response.json()
    return data.data
  } catch (error) {
    console.error('Error fetching branding config:', error)
    return null
  }
}

/**
 * Update branding configuration
 */
export async function updateBrandingConfig(config: Partial<BrandingConfig>): Promise<{ success: boolean; message?: string }> {
  try {
    const response = await fetchWithRefresh('/api/agency-branding/config', {
      method: 'PUT',
      credentials: 'include',
      headers: addCSRFHeader({
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify(config)
    })

    const data = await response.json()

    if (!response.ok) {
      return { success: false, message: data.message || 'Failed to update branding' }
    }

    return { success: true, message: data.message || 'Branding updated successfully' }
  } catch (error) {
    console.error('Error updating branding config:', error)
    return { success: false, message: 'Network error occurred' }
  }
}

/**
 * Upload logo or favicon
 */
export async function uploadLogo(file: File, type: 'logo' | 'favicon' = 'logo'): Promise<{
  success: boolean
  filename?: string
  logo_url?: string
  message?: string
}> {
  try {
    const formData = new FormData()
    formData.append('logo', file)
    formData.append('type', type)

    const response = await fetchWithRefresh('/api/agency-branding/logo', {
      method: 'POST',
      credentials: 'include',
      headers: addCSRFHeader({}), // Don't set Content-Type for FormData - browser sets it with boundary
      body: formData
    })

    const data = await response.json()

    if (!response.ok) {
      return { success: false, message: data.message || 'Failed to upload logo' }
    }

    return {
      success: true,
      filename: data.data?.filename,
      logo_url: data.data?.logo_url,
      message: data.message || 'Logo uploaded successfully'
    }
  } catch (error) {
    console.error('Error uploading logo:', error)
    return { success: false, message: 'Network error occurred' }
  }
}

/**
 * Delete logo or favicon
 */
export async function deleteLogo(type: 'logo' | 'favicon' = 'logo'): Promise<{ success: boolean; message?: string }> {
  try {
    const response = await fetchWithRefresh(`/api/agency-branding/logo?type=${type}`, {
      method: 'DELETE',
      credentials: 'include',
      headers: addCSRFHeader({
        'Content-Type': 'application/json',
      })
    })

    const data = await response.json()

    if (!response.ok) {
      return { success: false, message: data.message || 'Failed to delete logo' }
    }

    return { success: true, message: data.message || 'Logo deleted successfully' }
  } catch (error) {
    console.error('Error deleting logo:', error)
    return { success: false, message: 'Network error occurred' }
  }
}

/**
 * Update color scheme
 */
export async function updateColorScheme(colors: Partial<BrandingConfig['color_scheme']>): Promise<{
  success: boolean
  custom_css?: string
  message?: string
}> {
  try {
    const response = await fetchWithRefresh('/api/agency-branding/color-scheme', {
      method: 'PUT',
      credentials: 'include',
      headers: addCSRFHeader({
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify(colors)
    })

    const data = await response.json()

    if (!response.ok) {
      return { success: false, message: data.message || 'Failed to update colors' }
    }

    return {
      success: true,
      custom_css: data.data?.custom_css,
      message: data.message || 'Colors updated successfully'
    }
  } catch (error) {
    console.error('Error updating color scheme:', error)
    return { success: false, message: 'Network error occurred' }
  }
}

/**
 * Preview branding changes without saving
 */
export async function previewBranding(config: Partial<BrandingConfig>): Promise<BrandingConfig | null> {
  try {
    const response = await fetchWithRefresh('/api/agency-branding/preview', {
      method: 'POST',
      credentials: 'include',
      headers: addCSRFHeader({
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify(config)
    })

    if (!response.ok) {
      throw new Error(`Failed to preview branding: ${response.status}`)
    }

    const data = await response.json()
    return data.data?.preview
  } catch (error) {
    console.error('Error previewing branding:', error)
    return null
  }
}

/**
 * Reset branding to defaults
 */
export async function resetBranding(): Promise<{ success: boolean; message?: string }> {
  try {
    const response = await fetchWithRefresh('/api/agency-branding/reset', {
      method: 'POST',
      credentials: 'include',
      headers: addCSRFHeader({
        'Content-Type': 'application/json',
      })
    })

    const data = await response.json()

    if (!response.ok) {
      return { success: false, message: data.message || 'Failed to reset branding' }
    }

    return { success: true, message: data.message || 'Branding reset to defaults' }
  } catch (error) {
    console.error('Error resetting branding:', error)
    return { success: false, message: 'Network error occurred' }
  }
}

/**
 * Get logo URL for display with cache busting
 */
export function getLogoUrl(filename?: string): string | null {
  if (!filename) return null
  // Add timestamp for cache busting to ensure fresh images
  const timestamp = Date.now()
  const API_BASE_URL = process.env.REACT_APP_API_URL || ''
  return `${API_BASE_URL}/api/agency-branding/assets/${filename}?t=${timestamp}`
}

/**
 * Check if logo file exists and is accessible
 */
export async function checkLogoExists(filename?: string): Promise<boolean> {
  if (!filename) return false

  try {
    const response = await fetchWithRefresh(`/api/agency-branding/assets/${filename}`, {
      method: 'HEAD',
      credentials: 'include'
    })
    return response.ok
  } catch (error) {
    console.error('Error checking logo existence:', error)
    return false
  }
}